import React from 'react'
import { tv } from 'tailwind-variants'
import { Archive, Audit, Code, FileStack, Info, Key, Palette, Users, Webhook } from 'components/icons'
import { rowButtonProps } from 'components/sidebar/rowButton'

// Each hub settings section's icon, by its name.
const SECTION_ICONS: Record<string, React.FC<{ className?: string }>> = {
  Information: Info,
  Styling: Palette,
  Updates: FileStack,
  Archived: Archive,
  Audit: Audit,
  Members: Users,
  Webhooks: Webhook,
  Secrets: Key,
  Scripts: Code
}

// What a section is called where its name alone would say too little. Its id (the URL's ?tab= and the
// product tours' hooks) stays the short name.
const SECTION_LABELS: Record<string, string> = {
  Archived: 'Archived files',
  Updates: 'Bulk updates'
}

// In the manner of shadcn/ui's settings: the sections listed down a column beside the
// settings, each an icon and a label, the open one on a rounded highlight. When the settings page is
// narrow (under 56rem, as beside an open sidebar on a small laptop) the list runs across the top
// instead, scrolling sideways, so the settings keep the width.
const sectionNav = tv({
  slots: {
    list: 'flex flex-col gap-0.5 shrink-0 w-[200px] p-2 overflow-y-auto bg-surface border-r border-divider @max-4xl:flex-row @max-4xl:w-full @max-4xl:overflow-x-auto @max-4xl:overflow-y-hidden @max-4xl:border-r-0 @max-4xl:border-b',
    item: 'flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-[13px] font-medium text-body cursor-pointer select-none whitespace-nowrap outline-none transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover @max-4xl:w-auto [&_svg]:shrink-0'
  },
  variants: {
    selected: {
      true: { item: 'bg-surface-hover' }
    }
  }
})

interface SectionNavProps {
  sections: string[]
  selectedSection: string
  onClick: (section: string) => void
}

const SectionNav: React.FC<SectionNavProps> = ({ sections, selectedSection, onClick }) => {
  const styles = sectionNav()

  return (
    <nav aria-label="Hub settings" className={styles.list()}>
      {sections.map((section) => {
        const Icon = SECTION_ICONS[section]
        const selected = section === selectedSection
        return (
          <div
            key={section}
            // Product tours point at the sections by these.
            data-tour={`nav-section-${section.toLowerCase().replace(/\s+/g, '-')}`}
            aria-current={selected ? 'page' : undefined}
            className={sectionNav({ selected }).item()}
            {...rowButtonProps(() => onClick(section))}
          >
            {Icon && <Icon />}
            {SECTION_LABELS[section] ?? section}
          </div>
        )
      })}
    </nav>
  )
}

export default SectionNav
